import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import CardShell from '../components/CardShell';
import { useForecastData, buildForecastModel, buildTreasury } from './forecastModel';
import './ForecastSummary.css';

/* Cash flow prévisionnel — méthode indirecte DEPUIS LE RÉSULTAT NET.
   Convention : (+) encaissement / (−) décaissement. 12 mois figés (réel pour les
   mois clôturés, prévision ensuite), trait fin entre réel et prév.

   Investissement et financement = sections de lignes AJOUTABLES sur la prévision :
   chaque ligne porte un sens (+) encaissement / (−) décaissement (le bouton force
   le signe). Sous-totaux « Cash flow investissement » et « Financement ». Les mois
   clôturés restent le réel ancré sur le bilan → la trésorerie de fin de mois colle
   au bilan. Variation totale = opérationnel + investissement + financement.
   Saisies persistées en base (tenant × année) via le hub. */

export default function ForecastCashflowTab({ activeTenant, year, assumptions, unit = 'kchf', onReady }) {
  const { t } = useTranslation();
  const p = useP();
  const monthLabels = useMemo(
    () => Array.from({ length: 12 }, (_, i) => t(`common:months.${i + 1}`)),
    [t],
  );
  const tid = activeTenant?.id;
  const { plView, bsCur, bsPrev, bsPrev2, plPrev, fteCur, ftePrev, loading } = useForecastData(tid, year);

  /* LE BILAN PORTE DÉJÀ LES POSITIONS DU PLAN (2026-09-27) : la liquidité
     vient du plan de liquidité en Python, créances, dettes et dette sociale
     sont roulées par le serveur. Plus rien à surcharger ici. */
  const patchedBs = bsCur;

  const m = useMemo(
    () => buildForecastModel(plView, patchedBs, bsPrev, plPrev, assumptions, { bsPrev2, fteCur, ftePrev }),
    [plView, patchedBs, bsPrev, bsPrev2, plPrev, fteCur, ftePrev, assumptions.dso, assumptions.dpo, assumptions.dio, assumptions.pac],
  );
  useEffect(() => { if (!loading && onReady) onReady(); }, [loading]); // eslint-disable-line

  // Groupement des détails (fonds de roulement, investissement) — clic sur le
  // sous-total, groupé par défaut (détail masqué).
  const [grouped, setGrouped] = useState({ bfre: true, autres: true });
  const toggleGroup = (k) => setGrouped((prev) => ({ ...prev, [k]: !prev[k] }));

  if (loading && !m) return <p className="fps-loading">{t('common:status.loading')}</p>;
  if (!m) return <p className="fps-loading">{p('Aucune donnée prévisionnelle pour cette année.')}</p>;

  const c = m.cash, cc = m.closedCount;
  const idx = Array.from({ length: 12 }, (_, i) => i);
  const sum = (arr) => arr.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
  const div = unit === 'chf' ? 1 : 1000;
  const fmt = (v) => { if (v == null || !Number.isFinite(v)) return '–'; return (Math.round(v / div) + 0).toLocaleString('de-CH'); };

  // ── Cascade de trésorerie (opérationnel + investissement/financement du bilan).
  const opCf = idx.map((i) => c.opCf[i]);
  const { investSub, financingSub, totalVar, soldeInit, soldeFin, oLiq } = buildTreasury(c, null, cc);

  // Détail invest / financement : isole les NOUVELLES acquisitions / nouvelles
  // lignes (des plans du Bilan) du reste (variation des positions existantes :
  // cessions, corrections, remboursements) — pour ne pas masquer une vente par
  // une acquisition. Le reste = sous-total − nouveau.
  const bsRowsC = patchedBs?.rows || [];
  const planM = (code, key = 'months') => idx.map((i) => { const r = bsRowsC.find((x) => x.code === code); return Number((r?.[key] || r?.months)?.[String(i + 1)]) || 0; });
  const planNAm = planM('plan_new_asset', 'gross_months');   // CAPEX brut (sortie de cash ; amort non-cash)
  const planFinm = planM('plan_financing');
  const invNew = idx.map((i) => -(planNAm[i] - (i === 0 ? 0 : planNAm[i - 1])));
  const finNew = idx.map((i) => (planFinm[i] - (i === 0 ? 0 : planFinm[i - 1])));
  const invExist = idx.map((i) => (investSub[i] || 0) - invNew[i]);
  const finExist = idx.map((i) => (financingSub[i] || 0) - finNew[i]);

  // ── Rendu : table compacte 12 mois figés + Total (trait fin réel | prév.) ────
  const sepCls = (i) => (i === cc && cc > 0 ? ' fps-cmp-sepl' : '');
  const numCls = (v, i) => `fps-cmp-num${Number(v) < 0 ? ' fps-cmp-neg' : ''}${sepCls(i)}`;

  const valRow = (label, arr, kind, totalOverride) => {
    const tot = totalOverride === undefined ? sum(arr) : totalOverride;
    return (
      <tr className={kind ? `fps-cmp-row--${kind}` : ''}>
        <td className="fps-cmp-lbl">{label}</td>
        {idx.map((i) => <td key={i} className={numCls(arr[i], i)}>{fmt(arr[i])}</td>)}
        <td className={`${numCls(tot)} fps-cmp-key`}>{fmt(tot)}</td>
      </tr>
    );
  };

  // Sous-total de fonds de roulement, cliquable pour grouper / dégrouper le détail
  // (sans chevron : la ligne entière est le bouton).
  const groupRow = (label, arr, key) => {
    const tot = sum(arr);
    return (
      <tr className="fps-cf-grouprow" onClick={() => toggleGroup(key)}
        title={grouped[key] ? p('Cliquer pour voir le détail par compte') : p('Cliquer pour regrouper')}>
        <td className="fps-cmp-lbl">{label}<span className="fps-cf-groupmark">{grouped[key] ? '＋' : '－'}</span></td>
        {idx.map((i) => <td key={i} className={numCls(arr[i], i)}>{fmt(arr[i])}</td>)}
        <td className={`${numCls(tot)} fps-cmp-key`}>{fmt(tot)}</td>
      </tr>
    );
  };


  // Ligne de solde : fond rouge léger sur les cellules négatives (creux de liquidité).
  // Ligne de solde : négatif en rouge gras ; pas de total (colonne vide) ; mois
  // repère (début janvier / fin décembre) en gras.
  const soldeRow = (label, arr, boldIdx, gaptop) => (
    <tr className={`fps-cf-solderow${gaptop ? ' fps-cf-gaptop' : ''}`}>
      <td className="fps-cmp-lbl">{label}</td>
      {idx.map((i) => (
        <td key={i} className={`${numCls(arr[i], i)}${Number(arr[i]) < 0 ? ' fps-cf-solde-neg' : ''}${i === boldIdx ? ' fps-cf-solde-bold' : ''}`}>{fmt(arr[i])}</td>
      ))}
      <td className="fps-cmp-num fps-cmp-key" />
    </tr>
  );

  return (
    <div className="fps-synth">
      <CardShell className="report-card" title={t('phrases:p_96e08a3b9f')}
            subtitle={t('phrases:p_2abbd36a66')}
            viewControls={(
              <button type="button" className="fps-cf-groupbtn"
                onClick={() => { const all = grouped.bfre && grouped.autres; setGrouped({ bfre: !all, autres: !all }); }}>
                {(grouped.bfre && grouped.autres) ? p('Tout dégrouper') : p('Tout grouper')}
              </button>
            )}>
        <div className="fps-cmp-wrap">
          {/* Colonnes calées sur le contenu (comme le compte de résultat) :
              elles s'élargissent pour les nombres CHF, table pleine largeur.
              Police 13px (base fps-cmp, sans --compact) pour s'aligner sur les
              autres onglets ; fps-cf-tbl = hauteur de ligne + bordures scopées. */}
          <table className="fps-cmp fps-cmp--auto fps-cf-tbl">
            <colgroup>
              <col className="fps-col-lbl" />
              {idx.map((i) => <col key={i} />)}
              <col className="fps-col-total" />
            </colgroup>
            <thead>
              <tr>
                <th className="fps-cmp-lbl" rowSpan={2}> </th>
                <th className="fps-cmp-grp" colSpan={13}>{year}</th>
              </tr>
              <tr>
                {idx.map((i) => (
                  <th key={i} className={`fps-cmp-num${sepCls(i)}`}>
                    {monthLabels[i]}<span className={`fps-cmp-colsub ${i < cc ? 'is-reel' : 'is-prev'}`}>{i < cc ? p('réel') : p('prév.')}</span>
                  </th>
                ))}
                <th className="fps-cmp-num fps-cmp-key">{p('Total')}</th>
              </tr>
            </thead>
            <tbody>
              {valRow(p('Résultat net'), idx.map((i) => c.netResult[i]), 'bold')}
              {valRow(p('+ Amortissements'), idx.map((i) => c.amort[i]))}
              {valRow(p("Capacité d'autofinancement"), idx.map((i) => (Number(c.netResult[i]) || 0) + (Number(c.amort[i]) || 0)), 'bold')}
              {!grouped.bfre && (c.wcRows || []).filter((r) => r.isBfre).map((r, k) => (
                <React.Fragment key={`bfre-${k}`}>{valRow(r.label, idx.map((i) => r.cash[i]), 'det')}</React.Fragment>
              ))}
              {groupRow(p('± Variation BFRE'), idx.map((i) => (c.bfreCash ? c.bfreCash[i] : 0)), 'bfre')}
              {!grouped.autres && (c.wcRows || []).filter((r) => !r.isBfre).map((r, k) => (
                <React.Fragment key={`afr-${k}`}>{valRow(r.label, idx.map((i) => r.cash[i]), 'det')}</React.Fragment>
              ))}
              {groupRow(p('Autres éléments du fonds de roulement'), idx.map((i) => (c.autresCash ? c.autresCash[i] : 0)), 'autres')}
              {valRow(p('Cash flow opérationnel'), opCf, 'flux')}
              {/* Investissement : positions existantes (cessions…) vs nouvelles acquisitions. */}
              {valRow(p('Variation des actifs existants'), invExist, 'det')}
              {valRow(p('Nouveaux actifs (acquisitions)'), invNew, 'det')}
              {valRow(p('Cash flow des investissements'), investSub, 'flux')}
              {/* Financement : lignes existantes (remboursements…) vs nouvelles lignes. */}
              {valRow(p('Variation des lignes existantes'), finExist, 'det')}
              {valRow(p('Nouvelles lignes de financement'), finNew, 'det')}
              {valRow(p('Cash flow du financement'), financingSub, 'flux')}
              {valRow(p('Variation totale de trésorerie'), totalVar, 'cftotal')}
              {soldeRow(p('Solde initial de trésorerie'), soldeInit, 0, true)}
              {soldeRow(p('Solde final de trésorerie'), soldeFin, 11, false)}
            </tbody>
          </table>
        </div>
      </CardShell>
    </div>
  );
}
